import { useCallback, useEffect, useState } from "react";

import { useTranslationApp } from "../../../lib/i18next";
import {
  LOADING_CONTENT,
  MODAL_GALLERY_EMPTY_CONTENT,
} from "../../../i18n/keysTranslations";
import { IMAGE_FORMATS } from "../../../utils/constants";
import { getPathDatabaseByRole } from "../../../actions/auth";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useRebrandBanner } from "../../../hooks/useRebrandBanner";

import { useGalleryFromUrlSelection } from "./useGalleryFromUrlSelection";
import { useGalleryFromWebsiteUrl } from "./useGalleryFromWebsiteUrl";
import ModalGalleryFromUrlLayout from "./ModalGalleryFromUrlLayout";

const ModalGalleryFromUrl = ({
  modalOpen,
  onCloseModal,
  onCallbackSelectedItems,
  fetchUrl,
  multiple = true,
  maxSelections = null,
  acceptedFormats = IMAGE_FORMATS,
  minDimensions = {},
  titleKey,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const pathStorageWebsiteImages = userID ? `socialMedia/${userID}/` : "";

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const isPlanActive = useSelectorApp((state) => state?.payments?.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );
  const { totalTopOffsetPx } = useRebrandBanner();

  const websiteUrl = useGalleryFromWebsiteUrl({
    dispatch,
    fetchUrl,
    isPlanActive,
    isTrial,
    pathStorage: pathStorageWebsiteImages,
    setData,
    setError,
    setIsLoading,
    setIsUploading,
  });

  const {
    resetSelection,
    onSelectItem,
    selectedGalleryIndexes,
    hasSelection,
    getSelectedItems,
    galleryMultiple,
    usePickerMode,
    setSelectedItems,
  } = useGalleryFromUrlSelection({
    galleryDisplayData: data,
    multiple,
    maxSelections,
  });

  const onGetInitialData = useCallback(async () => {
    resetSelection();
    await websiteUrl.onGetInitialData();
    //eslint-disable-next-line
  }, [resetSelection, websiteUrl.onGetInitialData]);

  const onFailedLoadUrl = (index) => {
    websiteUrl.onFailedLoadUrl(index, setSelectedItems);
  };

  const onSendSelectedItems = useCallback(async () => {
    const itemsToProcess = getSelectedItems();
    if (!itemsToProcess.length) return;

    await websiteUrl.onSendSelectedItems(itemsToProcess, {
      onCallbackSelectedItems,
      onCloseModal,
    });
  }, [getSelectedItems, onCallbackSelectedItems, onCloseModal, websiteUrl]);

  useEffect(() => {
    if (!modalOpen) return;
    onGetInitialData();
  }, [modalOpen, onGetInitialData]);

  return (
    <ModalGalleryFromUrlLayout
      t={t}
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      titleKey={titleKey}
      isLoading={isLoading}
      loadingMessageKey={LOADING_CONTENT}
      error={error}
      totalTopOffsetPx={totalTopOffsetPx}
      emptyContentKey={MODAL_GALLERY_EMPTY_CONTENT}
      galleryDisplayData={data}
      hasSelection={hasSelection}
      isUploading={isUploading}
      showUploadingFooter={true}
      onConfirmSelection={onSendSelectedItems}
      galleryProps={{
        data,
        onCloseModal,
        lastItemRef: undefined,
        loadLastItem: true,
        onFailedLoadUrl,
        onSelectItem,
        selectedItems: selectedGalleryIndexes,
        onSelectPreview: () => {},
        multiple: galleryMultiple,
        overlayPickerMode: usePickerMode,
        selectedItemsArray: usePickerMode ? selectedGalleryIndexes : [],
        showSourceSelector: false,
        showFiltersSelector: false,
        showRating: false,
        acceptedFormats,
        minDimensions,
      }}
    />
  );
};

export default ModalGalleryFromUrl;
